import { Box, Button, Columns, Container, Content, Heading, Hero, Icon, Section } from 'react-bulma-components';
import {
  faBookOpen,
  faChalkboardTeacher,
  faHeadphonesAlt,
  faLayerGroup,
  faMusic,
  faQuoteLeft,
  faRoute,
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { colors } from '../styles';
import { navigate } from '@reach/router';
import { useAuth } from 'react-oidc-context';

const FEATURES = [
  {
    icon: faRoute,
    title: 'Step by step, song by song',
    description: "Every jazz standard is broken into steps that build on each other — start simple, and the level rises as you go.",
  },
  {
    icon: faLayerGroup,
    title: 'Exercises built for the song',
    description: "Practice drills written specifically for the tune you're learning, not generic scale patterns.",
  },
  {
    icon: faBookOpen,
    title: 'Go deeper with Facts',
    description: 'Dedicated lessons on scales, phrasing, modes, mastering the blues, and more — whenever you want to dig in.',
  },
  {
    icon: faHeadphonesAlt,
    title: 'Play along, at your pace',
    description: 'Backing tracks for every song and exercise, with playback speed you control until you can hit full tempo.',
  },
  {
    icon: faMusic,
    title: 'Any instrument, no transposing by hand',
    description: 'Set your instrument once and everything transposes for you automatically.',
  },
  {
    icon: faChalkboardTeacher,
    title: 'Built by working jazz musicians',
    description: 'Every song, exercise, and lesson is written by professional, experienced jazz musicians — not generated or outsourced.',
  },
];

// Placeholder quotes for layout/design purposes only — replace with real,
// verifiable customer testimonials before this page is shown to real
// visitors. Presenting invented quotes as genuine reviews would be
// misleading, so keep attributions generic (first name + instrument) until
// then, not fabricated full identities.
const TESTIMONIALS = [
  {
    quote: "For the first time, practice actually feels connected to playing. I'm not just running scales anymore — I understand what I'm doing when I solo.",
    name: 'Erik',
    detail: 'Tenor sax',
  },
  {
    quote: "The step-by-step approach is what years of theory books never gave me. I can hear myself getting better every single week.",
    name: 'Maria',
    detail: 'Guitar',
  },
  {
    quote: "Slowing the play-along tracks down let me actually hear my own mistakes instead of just plowing through them. That alone was worth it.",
    name: 'David',
    detail: 'Trumpet',
  },
];

const STEPS = [
  {
    title: 'Pick a song',
    description: 'Choose a jazz standard that matches where you are today.',
  },
  {
    title: 'Work through the steps',
    description: "Learn the changes, the melody, and the vocabulary to improvise over it — one step at a time.",
  },
  {
    title: 'Practice and play along',
    description: 'Drill the exercises, then play along with the backing track at a speed you can handle.',
  },
];

const StartPage = () => {
  const auth = useAuth();

  const getStarted = () => {
    if (auth.isAuthenticated) {
      navigate('/songs');
    } else {
      auth.signinRedirect();
    }
  };

  return (
    <>
      <Hero color="primary" size="medium">
        <Hero.Body>
          <Container>
            <Heading subtitle size={6} textTransform="uppercase" style={{ letterSpacing: '2px' }}>
              For jazz musicians, on any instrument
            </Heading>
            <Heading size={2}>
              Learn to improvise over jazz standards — one song, one step at a time.
            </Heading>
            <Heading subtitle size={5} style={{ maxWidth: '640px', marginTop: '1rem' }}>
              Step-by-step lessons, exercises built for the tune you're playing, and play-along
              tracks you can slow down until you're ready. TuneIn takes you from reading the chart
              to soloing over it.
            </Heading>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              <Button color="light" size="medium" onClick={getStarted}>
                Get started free
              </Button>
              <Button color="white" outlined size="medium" onClick={() => navigate('/songs')}>
                Browse Songs
              </Button>
              <Button color="white" outlined size="medium" onClick={() => navigate('/facts')}>
                Browse Facts
              </Button>
            </div>
          </Container>
        </Hero.Body>
      </Hero>

      <Section>
        <Container>
          <Heading textAlign="center">Everything you need to actually learn to improvise</Heading>
          <Columns multiline style={{ marginTop: '1rem' }}>
            {FEATURES.map(feature => (
              <Columns.Column size={4} key={feature.title}>
                <Box>
                  <Icon size="large">
                    <FontAwesomeIcon icon={feature.icon} size="2x" color={colors.primary} />
                  </Icon>
                  <Heading size={5} style={{ marginTop: '0.75rem' }}>{feature.title}</Heading>
                  <Content>{feature.description}</Content>
                </Box>
              </Columns.Column>
            ))}
          </Columns>
        </Container>
      </Section>

      <Section style={{ background: colors.background }}>
        <Container>
          <Heading textAlign="center">How it works</Heading>
          <Columns style={{ marginTop: '1rem' }}>
            {STEPS.map((step, index) => (
              <Columns.Column key={step.title}>
                <Heading size={3} style={{ color: colors.primary }}>{index + 1}</Heading>
                <Heading size={5}>{step.title}</Heading>
                <Content>{step.description}</Content>
              </Columns.Column>
            ))}
          </Columns>
        </Container>
      </Section>

      <Hero color="dark" size="medium">
        <Hero.Body>
          <Container>
            <Columns centered>
              <Columns.Column size={8} style={{ textAlign: 'center' }}>
                <Heading size={3}>
                  You practice, but you still freeze when it's your turn to solo.
                </Heading>
                <Content size="medium" style={{ marginTop: '1.5rem' }}>
                  You know more than you did a year ago. And somehow, when the changes start
                  moving and it's actually your turn, you play it safe — the same few licks,
                  thinking instead of hearing, hoping nobody notices you're guessing.
                </Content>
                <Content size="medium">
                  That's not a talent problem. It's a practice problem — and it's exactly the gap
                  TuneIn was built to close. Standard by standard, until improvising finally feels
                  like speaking instead of translating.
                </Content>
              </Columns.Column>
            </Columns>
          </Container>
        </Hero.Body>
      </Hero>

      <Section>
        <Container>
          <Heading textAlign="center">What musicians are saying</Heading>
          <Columns multiline style={{ marginTop: '1rem' }}>
            {TESTIMONIALS.map(testimonial => (
              <Columns.Column size={4} key={testimonial.name}>
                <Box style={{ height: '100%' }}>
                  <Icon><FontAwesomeIcon icon={faQuoteLeft} color={colors.grey} /></Icon>
                  <Content style={{ marginTop: '0.75rem', fontStyle: 'italic' }}>
                    "{testimonial.quote}"
                  </Content>
                  <Content style={{ color: colors.textSecondary, fontWeight: 'bold' }}>
                    {testimonial.name} <span style={{ fontWeight: 'normal' }}>— {testimonial.detail}</span>
                  </Content>
                </Box>
              </Columns.Column>
            ))}
          </Columns>
        </Container>
      </Section>

      <Hero color="dark" size="small">
        <Hero.Body>
          <Container style={{ textAlign: 'center' }}>
            <Heading size={3}>Ready to start soloing?</Heading>
            <Heading subtitle size={5}>It's free to get started.</Heading>
            <Button color="primary" size="medium" onClick={getStarted} style={{ marginTop: '1rem' }}>
              Get started free
            </Button>
          </Container>
        </Hero.Body>
      </Hero>
    </>
  );
}

export default StartPage;
